import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationsAPI } from '../api/api';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import { 
  ClipboardText, 
  ArrowLeft, 
  SpinnerGap,
  CheckCircle,
  Clock,
  XCircle,
  Briefcase,
  MapPin,
  CurrencyDollar,
  CalendarBlank,
  Eye
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';

const MyApplications = () => {
  const navigate = useNavigate();
  const { t } = useWorkerLang();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getMyApplications();
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-2 border-emerald-300">
            <CheckCircle size={14} weight="bold" className="mr-1" /> {t('accepted')}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-700 border-2 border-red-300">
            <XCircle size={14} weight="bold" className="mr-1" /> {t('rejected')}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-700 border-2 border-amber-300">
            <Clock size={14} weight="bold" className="mr-1" /> {t('pending_status')}
          </Badge>
        );
    }
  };

  const filteredApplications = filter === 'all' 
    ? applications 
    : applications.filter(app => app.status === filter);

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 py-8" data-testid="my-applications-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white font-bold mb-4 transition-colors"
          >
            <ArrowLeft size={20} weight="bold" />
            {t('back')}
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2B4C6F] to-blue-700 rounded-xl flex items-center justify-center border-2 border-black shadow-[4px_4px_0px_#000]">
              <ClipboardText size={32} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-neutral-900 dark:text-white">
                {t('my_apps_title')}
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 font-medium">
                {t('my_apps_sub')}
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div 
            onClick={() => setFilter('all')}
            className={`bg-white dark:bg-neutral-800 border-2 rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'all' ? 'border-[#2B4C6F] shadow-[4px_4px_0px_#2B4C6F]' : 'border-neutral-200 dark:border-neutral-700 shadow-[4px_4px_0px_#000]'
            }`}
          >
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">{t('all')}</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.total}</p>
          </div>
          <div 
            onClick={() => setFilter('pending')}
            className={`bg-white dark:bg-neutral-800 border-2 rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'pending' ? 'border-amber-500 shadow-[4px_4px_0px_#f59e0b]' : 'border-neutral-200 dark:border-neutral-700 shadow-[4px_4px_0px_#000]'
            }`}
          >
            <p className="text-sm font-bold text-amber-600">{t('pending_status')}</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.pending}</p>
          </div>
          <div 
            onClick={() => setFilter('accepted')}
            className={`bg-white dark:bg-neutral-800 border-2 rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'accepted' ? 'border-emerald-500 shadow-[4px_4px_0px_#10b981]' : 'border-neutral-200 dark:border-neutral-700 shadow-[4px_4px_0px_#000]'
            }`}
          >
            <p className="text-sm font-bold text-emerald-600">{t('accepted')}</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.accepted}</p>
          </div>
          <div 
            onClick={() => setFilter('rejected')}
            className={`bg-white dark:bg-neutral-800 border-2 rounded-xl p-4 cursor-pointer transition-all ${
              filter === 'rejected' ? 'border-red-500 shadow-[4px_4px_0px_#ef4444]' : 'border-neutral-200 dark:border-neutral-700 shadow-[4px_4px_0px_#000]'
            }`}
          >
            <p className="text-sm font-bold text-red-600">{t('rejected')}</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.rejected}</p>
          </div>
        </div>

        {/* Applications List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-12 text-center shadow-[4px_4px_0px_#000]">
            <ClipboardText size={64} weight="light" className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
              {t('no_apps_in_view')}
            </h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">
              {t('try_clear')}
            </p>
            <button
              onClick={() => navigate('/browse-jobs')}
              className="px-6 py-3 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            >
              {t('browse_all_jobs')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => (
              <div 
                key={app._id}
                className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:shadow-[6px_6px_0px_#000] transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-black text-neutral-900 dark:text-white">
                        {app.job_title || 'Job Application'}
                      </h3>
                      {getStatusBadge(app.status)}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                      {app.company_name && (
                        <span className="flex items-center gap-1">
                          <Briefcase size={16} weight="bold" />
                          {app.company_name}
                        </span>
                      )}
                      {app.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={16} weight="bold" />
                          {app.location}
                        </span>
                      )}
                      {app.proposed_rate && (
                        <span className="flex items-center gap-1">
                          <CurrencyDollar size={16} weight="bold" />
                          {app.proposed_rate}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <CalendarBlank size={16} weight="bold" />
                        {t('applied_on')} {new Date(app.created_at).toLocaleDateString()}
                      </span>
                    </div>

                    {app.cover_letter && (
                      <p className="mt-3 text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2">
                        {app.cover_letter}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => navigate(`/job/${app.job_id}`)}
                    className="flex items-center gap-2 px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
                  >
                    <Eye size={18} weight="bold" />
                    {t('view_job')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyApplications;

